<script setup lang="ts">
import { ref, computed, h, cloneVNode, withDirectives, type VNode } from 'vue'
import { cn } from '~/lib/utils'
import Dialog from './ui/dialog/Dialog.vue'
import DialogContent from './ui/dialog/DialogContent.vue'
import DialogTitle from './ui/dialog/DialogTitle.vue'
import DialogDescription from './ui/dialog/DialogDescription.vue'
import Button from './ui/button/Button.vue'

export type ButtonVariant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'warning' | 'success'

export type DialogResult = 'cancel' | 'confirm' | 'close'

export interface DialogProps {
  title?: string | VNode
  description?: string | VNode
  body?: string | VNode
  component?: any // Custom Vue component
  componentProps?: Record<string, any> // Props for the custom component
  componentClass?: any // Class applied to the custom component root
  componentWrapperClass?: any // Class applied to the wrapper around the custom component
  hideButtons?: boolean // Hide default buttons when using custom component
  cancelButtonText?: string
  confirmButtonText?: string
  hideCancelButton?: boolean
  cancelButtonVariant?: ButtonVariant
  confirmButtonVariant?: ButtonVariant
  defaultFocusButton?: 'cancel' | 'confirm'
  confirmButtonClass?: string
  cancelButtonClass?: string
  dialogContentClass?: string
  onClose: (confirmedResult: DialogResult) => void
}

const props = withDefaults(defineProps<DialogProps>(), {
  title: '',
  description: '',
  body: '',
  component: undefined,
  componentProps: () => ({}),
  hideButtons: false,
  cancelButtonText: 'Cancel',
  confirmButtonText: 'Confirm',
  defaultFocusButton: 'cancel',
  hideCancelButton: false,
  cancelButtonVariant: 'secondary',
  confirmButtonVariant: 'default',
  confirmButtonClass: '',
  cancelButtonClass: '',
  dialogContentClass: '',
  componentClass: '',
  componentWrapperClass: '',
  onClose: () => { },
})

const focus = {
  mounted(el: HTMLButtonElement, binding) {
    nextTick(() => {
      if (!Object.hasOwnProperty.call(binding, 'value') || binding.value) {
        el.focus()
      }
    })
  },
}

const emit = defineEmits(['close'])

const dialogVisible = ref(true)
const result = ref<'cancel' | 'close' | 'confirm'>('close')

const titleElement = computed(() => {
  if (typeof props.title === 'string' || !props.title) {
    return props.title || ''
  }
  return props.title
})

const descriptionElement = computed(() => {
  if (typeof props.description === 'string' || !props.description) {
    return h('p', props.description || '')
  }
  return props.description
})

const bodyElement = computed(() => {
  if (typeof props.body === 'string' || !props.body) {
    return h('p', props.body || '')
  }
  return props.body
})

function confirmed() {
  result.value = 'confirm'
  dialogVisible.value = false
}

function alert() {
  dialogVisible.value = true
  return dialogVisible.value
}

function canceled() {
  result.value = 'cancel'
  dialogVisible.value = false
}

function close(retValue: DialogResult = 'close') {
  result.value = retValue
  dialogVisible.value = false
}

watch(dialogVisible, (v) => {
  if (!v) {
    emit('close', result.value)
  }
})

defineExpose({ alert, close })

const confirmButtonClass = computed(() => cn(props.defaultFocusButton === 'confirm' ? 'focus:ring-1 focus:ring-ring' : '', props.confirmButtonClass))
const cancelButtonClass = computed(() => cn(props.defaultFocusButton === 'cancel' ? 'focus:ring-1 focus:ring-ring' : '', props.cancelButtonClass))

const buttons = () => h('div', { class: 'flex justify-end gap-x-2' }, [
  !props.hideCancelButton && withDirectives(h(Button, {
    variant: props.cancelButtonVariant,
    onClick: canceled,
    size: 'sm',
    class: cancelButtonClass.value,
  }, () => props.cancelButtonText || 'Cancel'), [[focus, props.defaultFocusButton === 'cancel']]),
  withDirectives(h(Button, {
    variant: props.confirmButtonVariant,
    onClick: confirmed,
    size: 'sm',
    class: confirmButtonClass.value,
  }, () => props.confirmButtonText || 'Confirm'), [[focus, props.defaultFocusButton === 'confirm']]),
])

const root = () => h(Dialog, {
  open: dialogVisible.value,
  'onUpdate:open': (value: boolean) => { dialogVisible.value = value },
}, {
  default: () => h(DialogContent, {
    class: cn('p-2 md:p-4 w-full md:w-[450px] max-w-[98vw] md:max-w-3xl max-h-[80vh] overflow-auto', props.dialogContentClass),
  }, () => [
    (!props.component || props.title) && h('div', {}, [
      h(DialogTitle, { class: 'flex w-full text-lg' }, () => titleElement.value || 'Dialog'),
      h(DialogDescription, {
        class: props.description || (!props.component && props.body) ? 'whitespace-pre-wrap w-full flex text-xs' : 'sr-only',
      }, () => props.description ? cloneVNode(descriptionElement.value) : ''),
    ]),
    props.component
      ? h('div', { class: cn('p-0', props.componentWrapperClass) }, [
          h(props.component, {
            ...props.componentProps,
            class: cn(props.componentProps?.class, props.componentClass),
            onClose: () => close('close'),
          }),
        ])
      : cloneVNode(bodyElement.value, { class: 'whitespace-pre-wrap leading-6 text-sm' }),
    (!props.component || !props.hideButtons) && buttons(),
  ]),
})

</script>

<template>
  <root />
</template>
